// Katılımcı davet + iptal confirm modalları — design-ref "Davet modalı hata
// mesajları" + "İptal confirm copy" (plan-design-review 2026-07-23). Stil:
// GenerateConfirmModal.tsx / ReviewModals.tsx ile aynı desen (overlay + kart).

import { useState } from "react";
import type { InviteParticipantResponse } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { inviteParticipant } from "../../api/participants";

// design-ref "Davet modalı hata mesajları (D5)" — birebir TR metinler.
const INVITE_ERROR_MESSAGES: Record<string, string> = {
  email_internal_user: "Bu e-posta dahili bir kullanıcıya ait — müşteri olarak davet edilemez.",
  already_participant: "Bu kişi bu projeye zaten davetli.",
};
const INVITE_GENERIC_ERROR = "Davet gönderilemedi — tekrar deneyin.";

interface InviteParticipantModalProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onInvited: (result: InviteParticipantResponse) => void;
}

export function InviteParticipantModal(props: InviteParticipantModalProps): JSX.Element | null {
  const { projectId, open, onClose, onInvited } = props;
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function reset(): void {
    setEmail("");
    setFullName("");
    setError(null);
  }

  function handleClose(): void {
    if (loading) return;
    reset();
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const result = await inviteParticipant(projectId, {
        email: email.trim(),
        fullName: fullName.trim(),
      });
      reset();
      onInvited(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(INVITE_ERROR_MESSAGES[err.code] ?? INVITE_GENERIC_ERROR);
      } else {
        setError(INVITE_GENERIC_ERROR);
      }
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = email.trim().length > 3 && fullName.trim().length > 1 && !loading;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={handleClose}
    >
      <div
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", color: "var(--color-text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1" style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem" }}>
          Müşteri Katılımcı Davet Et
        </h2>
        <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
          Davet edilen kişi e-posta ile bir aktivasyon linki alır ve yalnızca bu projenin form
          yanıtlarını doldurabilir.
        </p>

        <label className="block text-xs mb-1" style={{ color: "var(--color-text-2)" }}>
          Ad Soyad
        </label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          disabled={loading}
          className="w-full p-2 rounded border text-sm mb-3"
          style={{ borderColor: "var(--color-border)", fontFamily: "var(--font-sans)" }}
          placeholder="Örn: Ayşe Yılmaz"
        />

        <label className="block text-xs mb-1" style={{ color: "var(--color-text-2)" }}>
          E-posta
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          className="w-full p-2 rounded border text-sm mb-3"
          style={{ borderColor: "var(--color-border)", fontFamily: "var(--font-sans)" }}
          placeholder="ornek@musteri.com"
        />

        {error && (
          <div
            className="rounded-md p-3 mb-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            Vazgeç
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {loading ? "Gönderiliyor…" : "Davet Et"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RevokeParticipantModalProps {
  open: boolean;
  participantName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

// design-ref "İptal confirm copy": "{ad} bu projeye erişimini kaybedecek — davet
// gerekirse yeniden gönderilebilir." — stil ReviewModals.tsx BulkApproveModal ile aynı.
export function RevokeParticipantModal(props: RevokeParticipantModalProps): JSX.Element | null {
  if (!props.open) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={props.onCancel}
    >
      <div
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2" style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>
          Katılımı İptal Et
        </h3>
        <p className="text-sm mb-4" style={{ color: "var(--color-text-2)" }}>
          <strong>{props.participantName}</strong> bu projeye erişimini kaybedecek — davet gerekirse
          yeniden gönderilebilir.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={props.onCancel}
            disabled={props.loading}
            className="px-3 py-1.5 rounded-md text-sm"
            style={{ border: "1px solid var(--color-border)" }}
          >
            Vazgeç
          </button>
          <button
            onClick={props.onConfirm}
            disabled={props.loading}
            className="px-3 py-1.5 rounded-md text-sm disabled:opacity-50"
            style={{ background: "var(--color-error)", color: "white", fontWeight: 600 }}
          >
            {props.loading ? "İşleniyor…" : "İptal Et"}
          </button>
        </div>
      </div>
    </div>
  );
}
